// What a unit puts out, as the cards show it: each weapon's basic-attack damage, and each of its abilities' output
// (damage, healing, or a hit %), computed the way inlineUnit.vue does with no tuning: against a generic 60-Faith target,
// before the target's evasion, elements and zodiac. A monster's abilities are its own kit (gd.monsterSkills); a
// human's are its class and secondary skills. Pure: `gd` = { charStats, itemschanged, notAbles, abilitiesInfo,
// monsterSkills }. For the hover (lib/unitTip.js).
import { unitCombatStats } from './unitStats.js';
import { weaponDamage, unarmedDamage } from './damage.js';
import { unitElementProfile } from './elements.js';
import { abilityDisplay, legacyAbilityFormula } from './abilityFormula.js';

export const GENERIC_FAITH = 60;

export function unitOutput(u, gd) {
  const { equipped, stats, paUnarmed } = unitCombatStats(u, gd.charStats, gd.itemschanged, gd.notAbles);
  const strengthen = unitElementProfile(equipped).strengthen;
  const sup = u.SupportSkill || '';
  const attackUp = sup === 'Attack Up', magicAttackUp = sup === 'Magic Attack Up';
  const martialArts = sup === 'Martial Arts' || u.Class === 'Monk';
  const swing = (slot, plus = 0) => {
    const item = equipped[slot];
    if (!item || !item.type || item.type === 'Shield') return null;
    const d = weaponDamage({ item, pa: stats.pa + plus, ma: stats.ma, speed: stats.speed, brave: u.Brave, faith: u.Faith,
      doublehand: sup === 'Doublehand', targetFaith: GENERIC_FAITH, attackUp, magicAttackUp, strengthen });
    return d == null ? null : String(d);
  };
  const unarmed = (plus = 0) => String(unarmedDamage({ paUnarmed: paUnarmed + plus, brave: u.Brave, isMartialArts: martialArts, attackUp }));

  const weapons = [];
  if (u.Gender !== 'Monster') {
    if (u.Mainhand) { const d = swing('Mainhand'); weapons.push({ name: u.Mainhand, out: d }); } else weapons.push({ name: 'Unarmed', out: unarmed() });
    if (u.Offhand && equipped.Offhand.type !== 'Shield') { const d = swing('Offhand'); if (d != null) weapons.push({ name: u.Offhand, out: d }); }
  }

  const ctx = { ma: stats.ma, pa: stats.pa, speed: stats.speed, brave: u.Brave, faith: u.Faith, targetFaith: GENERIC_FAITH,
    wp: equipped.Mainhand.wp, maxHp: stats.hp, paUnarmed };
  const mods = { strengthen, attackUp, magicAttackUp, martialArts };
  const one = (name) => {
    const charge = /^Charge\+(\d+)$/.exec(name);
    if (charge) return u.Mainhand ? swing('Mainhand', +charge[1]) : unarmed(+charge[1]);
    const info = gd.abilitiesInfo && gd.abilitiesInfo[name];
    const parsed = info ? abilityDisplay(info, ctx, mods) : '';
    if (parsed) return parsed;
    const legacy = legacyAbilityFormula(name, { ...ctx, weaponType: equipped.Mainhand.type });
    return legacy ? legacy.display : '';
  };
  const mon = u.Gender === 'Monster' && gd.monsterSkills && gd.monsterSkills[u.Class];
  const names = mon ? mon.map((n) => n.replace(/\*$/, '')) : [...(u.ClassSkills || []), ...(u.ExtraSkills || [])];
  const abilities = names.map((name) => ({ name, out: one(name) || '' }));
  return { stats, equipped, weapons, abilities };
}

// The spotlight cards' one-line summary of a unitOutput result (2026-10-24, people on phones wanted damage without
// opening the full card): every basic attack first, both hands for a dual wielder, "Unarmed" with no weapon, then its
// biggest damaging abilities, then one heal or hit % if there's room. At most `max` entries, never fewer than the
// unit's attacks. Each is { name, out, kind: 'attack' | 'dmg' | 'heal' | 'pct' }.
export const outValue = (s) => { const m = String(s || '').match(/\d+/g); return m ? Math.max(...m.map(Number)) : -1; };
const outKind = (s) => (/%/.test(s) ? 'pct' : /heal/i.test(s) ? 'heal' : 'dmg');

export function topHits(out, max = 5) {
  const attacks = out.weapons.map((w) => ({ name: w.name, out: w.out || '–', kind: 'attack' }));
  const abil = out.abilities.filter((a) => a.out).map((a) => ({ name: a.name, out: a.out, kind: outKind(a.out), n: outValue(a.out) }));
  const dmg = abil.filter((a) => a.kind === 'dmg').sort((a, b) => b.n - a.n);
  const other = abil.filter((a) => a.kind !== 'dmg').sort((a, b) => (a.kind === b.kind ? b.n - a.n : a.kind === 'heal' ? -1 : 1));
  let room = Math.max(0, max - attacks.length);
  const pickDmg = dmg.slice(0, Math.min(3, Math.max(0, room - (other.length && dmg.length ? 1 : 0))));
  room -= pickDmg.length;
  const pickOther = other.slice(0, Math.min(room, dmg.length ? 1 : 2));
  return [...attacks, ...pickDmg, ...pickOther].map(({ n, ...h }) => h);
}
